import { Box as MuiBox, FormControl, FormLabel, MenuItem, Select, Typography } from "@mui/material";
import { getDeviceProfile, GPUTier } from "@novorender/api";
import { Home } from "@procore/core-icons";
import { Box, Button, ConfirmModal, Modal, Spinner } from "@procore/core-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { getModelHomeView, saveModelHomeViewpoint } from "apis/procore/bimModelHomeView";
import { getBimModelRevisions } from "apis/procore/bimModelRevision";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToggleWithText } from "components/toggleWithText";
import { FeatureToggle } from "config/featureToggles";
import { StorageKey } from "config/storage";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectCurrentDrawing } from "features/miniMap/minimapSlice";
import { renderActions, selectDeviceProfile } from "features/render";
import { SelectionResolution } from "features/render/types";
import { viewpointCameraToCadCamera } from "features/render/utils";
import { UnitSystem } from "features/uom/spec";
import { useGetViewpoint } from "features/viewpoints/useGetViewpoint";
import { useSceneId } from "hooks/useSceneId";
import {
    explorerActions,
    selectIsAxisSphereVisible,
    selectIsJoysticksVisible,
    selectIsMiniMapVisible,
    selectIsProcoreMobileDesign,
    selectProcoreCompanyId,
    selectProcoreContext,
    selectProcoreProjectId,
    selectSelectionResolution,
    selectUnitSystem,
} from "slices/explorer";
import { persistFeatureToggle, useFeatureToggle } from "slices/featureToggles";
import { telemetry } from "telemetry";
import { saveToStorage } from "utils/storage";

function getTierKey(tier: GPUTier): string {
    switch (tier) {
        case 0:
            return "low";
        case 1:
            return "medium";
        case 2:
            return "high";
        case 3:
            return "ultra";
        default:
            return "medium";
    }
}

const DEVICE_PROFILES = [0, 1, 2, 3].map((tier) => getDeviceProfile(tier as GPUTier));

const SELECTION_RESOLUTION_OPTIONS = [
    { value: SelectionResolution.FirstObject, key: "firstObject" },
    { value: SelectionResolution.LastObject, key: "lastObject" },
] as const;

export function SettingsContent() {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const unitSystem = useAppSelector(selectUnitSystem);
    const deviceProfile = useAppSelector(selectDeviceProfile);
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const companyId = useAppSelector(selectProcoreCompanyId);
    const projectId = useAppSelector(selectProcoreProjectId);
    const procoreContext = useAppSelector(selectProcoreContext);
    const isMiniMapVisible = useAppSelector(selectIsMiniMapVisible);
    const isAxisSphereVisible = useAppSelector(selectIsAxisSphereVisible);
    const isProcoreMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const isJoysticksVisible = useAppSelector(selectIsJoysticksVisible);
    const isUpdateHomeViewEnabled = useFeatureToggle(FeatureToggle.UpdateHomeView);
    const miniMapAvailable = useAppSelector(selectCurrentDrawing) !== null;
    const sceneId = useSceneId();
    const {
        state: { view },
    } = useExplorerGlobals();
    const getViewpoint = useGetViewpoint();
    const [modalOpen, setModalOpen] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const currentTier = deviceProfile.tier;

    const handleUnitSystemChange = (value: string) => {
        const newSystem = value as UnitSystem;
        if (newSystem !== unitSystem) {
            dispatch(explorerActions.setUnitSystem(newSystem));
            saveToStorage(StorageKey.UnitSystem, newSystem);
        }
    };

    const handlePerformanceChange = (value: string) => {
        const tier = Number(value) as GPUTier;
        if (tier !== currentTier) {
            const profile = getDeviceProfile(tier);
            dispatch(renderActions.setDeviceProfile(profile));
        }
    };

    const handleSelectionResolutionChange = (value: string) => {
        const newResolution = value as SelectionResolution;
        if (newResolution !== selectionResolution) {
            dispatch(explorerActions.setSelectionResolution(newResolution));
            saveToStorage(StorageKey.SelectionResolution, newResolution);
        }
    };

    const handleMiniMapToggle = () => dispatch(persistFeatureToggle(FeatureToggle.MiniMapVisible, !isMiniMapVisible));
    const handleNavSphereToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.AxisSphereVisible, !isAxisSphereVisible));
    const handleJoysticksToggle = () =>
        dispatch(persistFeatureToggle(FeatureToggle.JoysticksVisible, !isJoysticksVisible));

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleConfirmHomeViewUpdate = async () => {
        if (isUpdating || view?.activeController.kind === "panorama" || !companyId || !projectId) {
            return;
        }

        dispatch(explorerActions.setSnackbarMessage(null));

        setIsUpdating(true);
        try {
            const [viewpointPayload, revisions] = await Promise.all([
                getViewpoint({ createSnapshot: true, snapshotWidth: 3840, snapshotHeight: 2160, snapshotType: "jpeg" }),
                getBimModelRevisions({
                    companyId,
                    projectId,
                    sceneId,
                    sceneVersionId: procoreContext?.sceneVersionId,
                }),
            ]);

            const resolvedSceneVersionId = procoreContext?.sceneVersionId ?? sceneId;
            const revision =
                revisions.find((r) => r.current_platform_scene_version_id === resolvedSceneVersionId) ??
                revisions.find((r) => r.current_platform_scene_id === sceneId);

            if (!revision?.bim_model_id) {
                throw new Error("Could not resolve model id from revision");
            }

            const platformSceneId = revision.current_platform_scene_id;
            if (!platformSceneId) {
                throw new Error("Could not resolve platform scene id from revision");
            }

            if (!viewpointPayload.snapshot) {
                throw new Error("Failed to create snapshot for home viewpoint");
            }

            const existingHomeView = await getModelHomeView({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
            });

            await saveModelHomeViewpoint({
                companyId,
                projectId,
                modelId: revision.bim_model_id,
                sceneId: platformSceneId,
                viewpoint: viewpointPayload,
                existingHomeView,
            });

            dispatch(renderActions.setInitialCamera(viewpointCameraToCadCamera(viewpointPayload.camera)));

            handleCloseModal();
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateSuccess"), closeAfter: 5000 }));
        } catch (e) {
            console.error("[Home View] Failed to update home view:", e);
            telemetry.trackCaughtException({
                source: "settingsContent.updateHomeView",
                category: "data",
                error: e,
                severity: "error",
            });
            dispatch(explorerActions.setSnackbarMessage({ msg: t("homeViewUpdateError"), closeAfter: 5000 }));
        } finally {
            setIsUpdating(false);
        }
    };

    const canUpdateHomeView = Boolean(view && companyId && projectId);

    return (
        <Box display="flex" flexDirection="column" gap="1rem" padding="1rem">
            <FormControl fullWidth size="small">
                <FormLabel sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>{t("measurementSystem")}</FormLabel>
                <Select
                    value={unitSystem}
                    onChange={(e) => handleUnitSystemChange(e.target.value)}
                    size="small"
                    fullWidth
                    data-testid="settings-measurement-system-select"
                >
                    <MenuItem value={UnitSystem.imperial}>{t("imperial")}</MenuItem>
                    <MenuItem value={UnitSystem.metric}>{t("metricMeters")}</MenuItem>
                    <MenuItem value={UnitSystem.metricMM}>{t("metricMillimeters")}</MenuItem>
                </Select>
            </FormControl>

            <FormControl fullWidth size="small">
                <FormLabel sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>{t("performance")}</FormLabel>
                <Select
                    value={currentTier}
                    onChange={(e) => handlePerformanceChange(String(e.target.value))}
                    size="small"
                    fullWidth
                    renderValue={(value) => {
                        const key = getTierKey(value as GPUTier);
                        return t(`performancePresets.${key}.name`);
                    }}
                    data-testid="settings-performance-select"
                >
                    {DEVICE_PROFILES.map((profile) => {
                        const presetKey = getTierKey(profile.tier);
                        return (
                            <MenuItem key={profile.tier} value={profile.tier}>
                                <Box>
                                    <Typography variant="body2">{t(`performancePresets.${presetKey}.name`)}</Typography>
                                    <Typography variant="body1" color="text.secondary">
                                        {t(`performancePresets.${presetKey}.description`)}
                                    </Typography>
                                </Box>
                            </MenuItem>
                        );
                    })}
                </Select>
            </FormControl>

            <FormControl fullWidth size="small">
                <FormLabel sx={{ fontWeight: 600, color: "text.primary", mb: 1 }}>{t("selectionResolution")}</FormLabel>
                <Select
                    value={selectionResolution}
                    onChange={(e) => handleSelectionResolutionChange(e.target.value)}
                    size="small"
                    fullWidth
                    renderValue={(value) => {
                        const option = SELECTION_RESOLUTION_OPTIONS.find((opt) => opt.value === value);
                        return option ? t(`selectionResolutionOptions.${option.key}.name`) : "";
                    }}
                    data-testid="settings-selection-resolution-select"
                >
                    {SELECTION_RESOLUTION_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            <Box>
                                <Typography variant="body2">
                                    {t(`selectionResolutionOptions.${option.key}.name`)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "normal" }}>
                                    {t(`selectionResolutionOptions.${option.key}.description`)}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <MuiBox
                display="flex"
                flexDirection="column"
                gap="0.5rem"
                sx={{
                    "& label[class*='StyledSwitchContainer']": {
                        border: "1px solid transparent !important",
                        boxShadow: "none !important",
                        outline: "none !important",
                    },
                }}
            >
                <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("showInViewer")}</FormLabel>
                {miniMapAvailable && (
                    <ToggleWithText checked={isMiniMapVisible} onChange={handleMiniMapToggle} label="miniMap" />
                )}
                {!isProcoreMobileDesign ? (
                    <ToggleWithText
                        checked={isAxisSphereVisible}
                        onChange={handleNavSphereToggle}
                        label="navigationSphere"
                    />
                ) : (
                    <ToggleWithText checked={isJoysticksVisible} onChange={handleJoysticksToggle} label="joysticks" />
                )}
            </MuiBox>

            {isUpdateHomeViewEnabled && (
                <>
                    <Box display="flex" flexDirection="column" gap="0.5rem">
                        <FormLabel sx={{ fontWeight: 600, color: "text.primary" }}>{t("homeView")}</FormLabel>
                        <Typography variant="body1" color="text.secondary">
                            {t("homeViewDescription")}
                        </Typography>
                        <Box>
                            <Button
                                variant="secondary"
                                icon={<Home />}
                                disabled={!canUpdateHomeView}
                                onClick={handleOpenModal}
                                data-testid="settings-update-home-view-button"
                            >
                                {t("updateHomeView")}
                            </Button>
                        </Box>
                    </Box>

                    <ConfirmModal
                        open={modalOpen}
                        headline={t("updateProjectHomeViewTitle")}
                        onClose={handleCloseModal}
                        width="sm"
                    >
                        <Modal.Body>
                            <Typography>{t("updateProjectHomeViewBody")}</Typography>
                        </Modal.Body>
                        <Modal.Footer>
                            <Modal.FooterButtons onClose={handleCloseModal}>
                                <Button variant="tertiary" onClick={handleCloseModal} disabled={isUpdating}>
                                    {t("cancel")}
                                </Button>
                                <Button variant="primary" onClick={handleConfirmHomeViewUpdate} disabled={isUpdating}>
                                    {isUpdating ? <Spinner size="sm" loading variant="button" /> : t("update")}
                                </Button>
                            </Modal.FooterButtons>
                        </Modal.Footer>
                    </ConfirmModal>
                </>
            )}
        </Box>
    );
}
